import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IoSend, IoChatbubbles, IoExpand } from 'react-icons/io5';
import api from '../api/axios';
import styles from './ChatWidget.module.css';

interface ChatMessage {
  id: number;
  userId: number;
  username: string;
  avatarUrl?: string;
  message: string;
  attachments?: string; // JSON string of attachment URLs
  createdAt: string;
}

interface ChatWidgetProps {
  currentUserId?: number;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ currentUserId }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUpRef = useRef<boolean>(false);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    isUserScrolledUpRef.current = !isAtBottom;
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get('/chat/messages?limit=20');
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    fetchMessages();
    
    // Poll for new messages every 5 seconds (less frequent for widget)
    const interval = setInterval(fetchMessages, 5000);
    
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      clearInterval(interval);
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (!isUserScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      await api.post('/chat/send', {
        message: newMessage.trim(),
        channelId: 'general'
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={styles.chatWidget}>
      <div className={styles.chatHeader}>
        <div className={styles.headerContent}>
          <IoChatbubbles className={styles.headerIcon} />
          <div>
            <h3 className={styles.headerTitle}>Community Chat</h3>
            <p className={styles.headerSubtitle}>Synced with Discord #general • Registered users only</p>
          </div>
        </div>
        <Link to="/chat" className={styles.expandButton}>
          <IoExpand />
        </Link>
      </div>

      <div className={styles.messagesContainer} ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <IoChatbubbles className={styles.emptyIcon} />
            <p className={styles.emptyText}>No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={styles.messageRow}>
              <img 
                src={msg.avatarUrl && (msg.avatarUrl.startsWith('http://') || msg.avatarUrl.startsWith('https://')) 
                  ? msg.avatarUrl 
                  : msg.avatarUrl 
                    ? `https://storage.tficorg.org/${msg.avatarUrl}` 
                    : 'https://ui-avatars.com/api/?name=' + encodeURIComponent(msg.username) + '&background=3b82f6&color=fff'} 
                alt={msg.username}
                className={styles.messageAvatar}
                onError={(e) => {
                  e.currentTarget.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(msg.username) + '&background=3b82f6&color=fff';
                }}
              />
              <div className={styles.messageContent}>
                <div className={styles.messageHeader}>
                  <span className={styles.messageUsername}>{msg.username}</span>
                  <span className={styles.messageTime}>{formatTime(msg.createdAt)}</span>
                </div>
                <p className={styles.messageText}>{msg.message}</p>
                {msg.attachments && (() => {
                  try {
                    const attachmentUrls = JSON.parse(msg.attachments);
                    return (
                      <div className={styles.attachments}>
                        {attachmentUrls.map((url: string, index: number) => (
                          <a 
                            key={index} 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.attachmentLink}
                          >
                            <img 
                              src={url} 
                              alt={`Attachment ${index + 1}`} 
                              className={styles.attachmentImage}
                            />
                          </a>
                        ))}
                      </div>
                    );
                  } catch (e) {
                    return null;
                  }
                })()}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {currentUserId ? (
        <form onSubmit={handleSendMessage} className={styles.messageForm}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className={styles.messageInput}
            maxLength={500}
          />
          <button 
            type="submit" 
            disabled={sending || !newMessage.trim()}
            className={styles.sendButton}
          >
            <IoSend />
          </button>
        </form>
      ) : (
        <div className={styles.loginPrompt}>
          <Link to="/login">Login to chat</Link>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
